import { useActiveAddress } from "@arweave-wallet-kit/react"
import { createDataItemSigner } from "@permaweb/aoconnect"
import type { dryrun } from "@permaweb/aoconnect"
import { QueryObserverResult, useQuery } from "@tanstack/react-query"

import { useEffect, useState } from "react"

import { AoInstance, useAo, Message } from "./ao"
import { RefreshFunction } from "./pool"
import { TOKEN_BALANCE_TIMEOUT, TOKEN_INFO_TIMEOUT, TOKEN_MIRRORS } from "../settings"
import { Tag, flattenTags, getTagValue } from "../utils/arweave"
import { isAddress } from "../utils/format"
import { createLogger } from "../utils/logger"

const logger = createLogger("token-api")

type DryRunResult = Awaited<ReturnType<typeof dryrun>>

/**
 * Helper function to perform dry run with retry
 * @param dryRunFn Function that performs the dry run
 * @param timeout Timeout in milliseconds
 * @param maxRetries Maximum number of retries
 * @param retryDelay Delay between retries in milliseconds
 */
async function performDryRunWithRetry(
  dryRunFn: () => Promise<DryRunResult>,
  timeoutMsg: string,
  timeout: number,
  maxRetries = 3,
  retryDelay = 3000,
): Promise<DryRunResult> {
  let lastError: any

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await Promise.race<DryRunResult>([
        dryRunFn(),
        new Promise((_, reject) =>
          setTimeout(() => reject(`Timeout: ${timeoutMsg}`), timeout),
        ),
      ])
    } catch (err) {
      lastError = err
      console.warn(`Dry run attempt ${attempt + 1}/${maxRetries + 1} failed:`, err)

      // Don't wait on the last attempt
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay))
      }
    }
  }

  throw lastError
}

/**
 * Get info about a token
 * @param tokenId address of the token process
 */
export async function getTokenInfo(
  tokenId: string,
  ao: AoInstance,
  timeout = TOKEN_INFO_TIMEOUT,
): Promise<TokenInfo> {
  if (!isAddress(tokenId)) {
    throw new Error(`Invalid tokenId ${tokenId}`)
  }

  const result = await performDryRunWithRetry(
    () => ao.dryrun({
      process: tokenId,
      tags: [{ name: "Action", value: "Info" }],
    }),
    `reading Info (${tokenId})`,
    timeout
  )

  if (result.Messages.length === 0) throw new Error(`No response from (get) Info (${tokenId})`)
  const tagMap = flattenTags(result.Messages[0].Tags)

  const denomination = parseInt(tagMap["Denomination"])
  if (isNaN(denomination)) throw new Error(`Denomination is not a number ${tokenId}`)
  if (!tagMap["Name"]) throw new Error(`Token name is missing ${tokenId}`)
  if (!tagMap["Ticker"]) throw new Error(`Token ticker is missing ${tokenId}`)

  return {
    id: tokenId,
    name: tagMap["Name"],
    ticker: tagMap["Ticker"],
    denomination: denomination,
    logo: tagMap["Logo"],
  }
}

/**
 * Result of token info message
 */
export interface TokenInfo {
  id: string
  denomination: number
  ticker: string
  logo: string
  name: string
}

// Balance cache for resilience
const balanceCache: Record<string, { value: bigint; timestamp: number }> = {}

// Rate limiting tracking
interface RateLimitRecord {
  count: number
  lastAttempt: number
  consecutiveErrors: number
}
const rateLimitRecords: Record<string, RateLimitRecord> = {}

// Check if token is being rate limited too heavily
function isRateLimited(tokenId: string): boolean {
  const record = rateLimitRecords[tokenId]
  if (!record) return false

  const now = Date.now()
  const timeSinceLastAttempt = now - record.lastAttempt

  // If we've had many consecutive errors and it's been less than 2 seconds
  // since our last attempt, consider this token rate limited
  return record.consecutiveErrors > 3 && timeSinceLastAttempt < 2000
}

// Record a rate limit occurrence for a token
function recordRateLimit(tokenId: string): void {
  const now = Date.now()
  const record = rateLimitRecords[tokenId] || { count: 0, lastAttempt: 0, consecutiveErrors: 0 }

  record.count++
  record.lastAttempt = now
  record.consecutiveErrors++

  rateLimitRecords[tokenId] = record
  logger.log(`Rate limit recorded for ${tokenId} (consecutive: ${record.consecutiveErrors})`)
}

// Clear rate limit record when successful
function clearRateLimit(tokenId: string): void {
  if (rateLimitRecords[tokenId]) {
    rateLimitRecords[tokenId].consecutiveErrors = 0
  }
}

// Determine if we should throw to trigger retry
function shouldThrowForRetry(tokenId: string): boolean {
  const record = rateLimitRecords[tokenId]
  if (!record) return true // If no record, allow throw

  // Only throw for retry if we've had fewer than 5 consecutive errors
  // This prevents constant retrying when the service is clearly unavailable
  return record.consecutiveErrors < 5
}

/**
 * Get balance for address
 * @param tokenId ID of the token
 * @param address Target address
 */
export async function getBalance(
  tokenId: string,
  address: string,
  ao: AoInstance,
  timeout = TOKEN_BALANCE_TIMEOUT,
): Promise<bigint> {
  // Generate a cache key
  const cacheKey = `${tokenId}_${address}`
  const now = Date.now()
  const cachedData = balanceCache[cacheKey]

  // If we have a cached value less than 2 minutes old, use it
  const CACHE_TTL = 2 * 60 * 1000 // 2 minutes in milliseconds
  if (cachedData && now - cachedData.timestamp < CACHE_TTL) {
    logger.log(`Using cached balance for ${tokenId}:`, cachedData.value.toString())
    return cachedData.value
  }

  // First, check if we are being rate limited too frequently
  if (isRateLimited(tokenId)) {
    logger.log(`Rate limiting detected for ${tokenId}, using cached data or default`)
    return cachedData?.value || 0n
  }

  try {
    // Wrap in AbortController to properly handle timeouts
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      logger.log(`Requesting balance for ${tokenId}...`)

      const res = await ao.dryrun({
        Owner: address,
        process: TOKEN_MIRRORS[tokenId] || tokenId,
        tags: [
          { name: "Action", value: "Balance" },
          { name: "Recipient", value: address },
        ],
      })

      // If we get here, request succeeded
      for (const msg of res.Messages as Message[]) {
        const balance = getTagValue("Balance", msg.Tags) || JSON.parse(msg.Data)
        const value = BigInt(balance)

        // Update cache
        balanceCache[cacheKey] = { value, timestamp: now }
        logger.log(`Balance fetched and cached for ${tokenId}: ${value.toString()}`)

        // Clear any rate limiting record
        clearRateLimit(tokenId)

        return value
      }

      logger.warn(`Balance response malformed for token ${tokenId}`)
      // If we have any cached data, use it as fallback
      if (cachedData) {
        logger.log(`Using stale cached balance for ${tokenId} after malformed response`)
        return cachedData.value
      }
      return 0n
    } finally {
      clearTimeout(timeoutId)
    }
  } catch (err) {
    // Handle abort/timeout errors
    if (err instanceof DOMException && err.name === "AbortError") {
      logger.error(`Timeout reading balance for token ${tokenId}`)
      if (cachedData) {
        logger.log(`Using stale cached balance for ${tokenId} after timeout`)
        return cachedData.value
      }
      return 0n
    }

    logger.error(`Failed to read balance for token ${tokenId}:`, err)

    // Check for 429/CORS errors (which often appear as TypeError: Failed to fetch)
    const is429Error =
      (err as any)?.status === 429 ||
      (err as any)?.statusCode === 429 ||
      (err instanceof Error &&
        (err.message.includes("429") ||
          err.message.includes("Too Many Requests") ||
          // CORS errors from rate limiting often show as "Failed to fetch"
          (err.message.includes("Failed to fetch") && err.name === "TypeError")))

    if (is429Error) {
      // Record the rate limiting
      recordRateLimit(tokenId)

      logger.log(`Detected 429/CORS error for ${tokenId}, trying fallback or cache`)

      // Use cache if available
      if (cachedData) {
        logger.log(`Using cached balance for ${tokenId} after 429/CORS error`)
        return cachedData.value
      }

      // We want to signal React Query to retry, but let's not spam if we keep getting rate limited
      if (shouldThrowForRetry(tokenId)) {
        logger.log(`Throwing 429 error for ${tokenId} to trigger retry`)
        throw new Error(`Rate limited (429): ${tokenId}`)
      } else {
        logger.log(`Too many 429 errors for ${tokenId}, returning default value`)
        return 0n
      }
    }

    // For other network errors, use cache or return 0
    if (cachedData) {
      logger.log(`Using stale cached balance for ${tokenId} after error`)
      return cachedData.value
    }

    return 0n
  }
}

/**
 * Token balance hook (integer balance)
 */
export function useBalance(id: string): [bigint, boolean, RefreshFunction] {
  // balance
  const [balance, setBalance] = useState(0n)

  // active address
  const activeAddr = useActiveAddress()

  // loading
  const [loading, setLoading] = useState(true)

  // ao instance
  const ao = useAo()

  /**
   * Load balance
   */
  async function load() {
    if (!activeAddr || !id || id === "") return
    setLoading(true)

    const retry = async (tries = 0) => {
      try {
        const bal = await getBalance(id, activeAddr, ao)

        setBalance(bal || 0n)
      } catch (e) {
        if (tries >= 5) return
        await retry(tries + 1)
      }
    }
    await retry()

    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [id, activeAddr, ao])

  return [balance, loading, load]
}

// Total supply cache for resilience
const totalSupplyCache: Record<string, { value: bigint; timestamp: number }> = {}

/**
 * Get token total supply
 */
export async function getTotalSupply(id: string, ao: AoInstance) {
  // Check cache first
  const now = Date.now()
  const cachedData = totalSupplyCache[id]

  // If we have a cached value less than 2 minutes old, use it
  const CACHE_TTL = 2 * 60 * 1000 // 2 minutes in milliseconds
  if (cachedData && now - cachedData.timestamp < CACHE_TTL) {
    console.log(`Using cached total supply for ${id}:`, cachedData.value.toString())
    return cachedData.value
  }

  try {
    const res = await ao.dryrun({
      process: id,
      tags: [{ name: "Action", value: "Total-Supply" }],
    })

    for (const msg of res.Messages as Message[]) {
      const totalSupply = getTagValue("Total-Supply", msg.Tags)
      if (totalSupply) {
        const value = BigInt(totalSupply)
        // Update cache
        totalSupplyCache[id] = { value, timestamp: now }
        return value
      }
    }

    // No valid total supply found
    console.warn(`No total supply found for token ${id}`)
    // If we have any cached data, use it as fallback
    if (cachedData) {
      console.log(`Using stale cached total supply for ${id}`)
      return cachedData.value
    }
    return 0n
  } catch (error) {
    console.error(`Error fetching total supply for token ${id}:`, error)

    // For errors, try to use cache as fallback
    if (cachedData) {
      console.log(`Using stale cached total supply for ${id} after error`)
      return cachedData.value
    }

    // For network errors, return 0n instead of throwing
    if (
      error instanceof Error &&
      (error.message.includes("Failed to fetch") ||
        error.message.includes("Network request failed"))
    ) {
      return 0n
    }

    // Re-throw other errors to allow React Query's retry mechanism to work
    throw error
  }
}

/**
 * Total supply hook
 */
export function useTotalSupply(
  tokenId: string,
): [bigint, boolean, () => Promise<QueryObserverResult<bigint, Error>>] {
  // ao instance
  const ao = useAo()

  const {
    data = 0n,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["total-supply", tokenId, ao],
    queryFn: async () => {
      try {
        return await getTotalSupply(tokenId, ao)
      } catch (error) {
        console.error("Error fetching total supply:", error)
        throw error // Let React Query handle retries
      }
    },
    enabled: !!tokenId,
    retry: 5,
    retryDelay: (attemptIndex) => {
      // Faster retry strategy
      const baseDelay = 500 // Start at 500ms
      const delay = Math.min(baseDelay * 1.5 ** attemptIndex, 5000)
      const jitter = delay * 0.1 * Math.random()
      return delay + jitter
    },
  })

  return [data, isLoading, refetch]
}

/**
 * Token transfer function
 */
export async function transfer(
  data: TransferParams,
  signer: ReturnType<typeof createDataItemSigner>,
  ao: AoInstance,
) {
  // execute the transfer
  return await ao.message({
    process: data.token,
    signer,
    tags: [
      { name: "Action", value: "Transfer" },
      { name: "Recipient", value: data.recipient },
      { name: "Quantity", value: data.quantity.toString() },
      ...(data.tags || []),
    ],
  })
}

interface TransferParams {
  token: string
  quantity: bigint
  recipient: string
  tags?: Tag[]
}

/**
 * Check a transfer result for errors
 */
export async function assertTransferResult(message: string, process: string, ao: AoInstance) {
  // read transfer result
  const res = await ao.result({ process, message })

  // transfer error
  if (res.Error) {
    throw new Error(res.Error)
  }

  // rejected transfer
  for (const msg of res.Messages as Message[]) {
    const action = getTagValue("Action", msg.Tags)

    if (action === "Transfer-Error") {
      throw new Error(getTagValue("Error", msg.Tags) || "Unknown transfer error")
    }
  }
}
